import Section from '../components/Section';
import ServiceCard from '../components/ServiceCard';
import Button from '../components/Button';

const Services = () => {
  const consultancyServices = [
    'Full operational performance reviews',
    'Sales process optimisation',
    'Customer journey mapping and improvement',
    'Aftersales performance enhancement',
    'Digital retailing strategy',
    'Margin and profitability analysis'
  ];

  const managementServices = [
    'Executive coaching for dealer principals and GMs',
    'Sales leadership development programmes',
    'Management team effectiveness reviews',
    'Talent identification and succession planning',
    'Recruitment support and interview training',
    'Performance management coaching'
  ];

  const salesEventServices = [
    'Weekend sales boost events',
    'Week-long intensive programmes',
    'Seasonal campaign support',
    'New model launch events',
    'Stock reduction initiatives',
    'End-of-quarter performance drives'
  ];

  return (
    <>
      {/* Page Header */}
      <section className="bg-summit-navy-dark text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold">Our Services</h1>
          <p className="text-xl text-gray-200 mt-4">
            Comprehensive consultancy, training, and support designed to drive dealership performance
          </p>
        </div>
      </section>

      {/* Consultancy Projects */}
      <Section>
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold text-summit-navy-dark mb-6">Performance Consultancy</h2>
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-summit-navy mb-3">The Challenge</h3>
              <p className="text-summit-grey">
                Dealerships often know performance could be better, but lack the internal resource or objectivity to identify root causes and implement sustainable improvements.
              </p>
            </div>
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-summit-navy mb-3">Summit Solution</h3>
              <p className="text-summit-grey">
                Our consultants conduct comprehensive operational reviews, identify key improvement opportunities, and work alongside management to implement changes that deliver measurable commercial outcomes.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-summit-navy mb-3">Commercial Outcome</h3>
              <p className="text-summit-grey">
                Improved profitability, enhanced operational efficiency, stronger customer satisfaction, and sustainable performance improvements that endure beyond the programme.
              </p>
            </div>
          </div>
          <div>
            <ServiceCard title="What We Cover" items={consultancyServices} />
          </div>
        </div>
      </Section>

      {/* Divider */}
      <div className="border-t border-gray-200"></div>

      {/* Management Development */}
      <Section background="grey">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="order-2 md:order-1">
            <ServiceCard title="Development Areas" items={managementServices} />
          </div>
          <div className="order-1 md:order-2">
            <h2 className="text-3xl font-bold text-summit-navy-dark mb-6">Management Development</h2>
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-summit-navy mb-3">The Challenge</h3>
              <p className="text-summit-grey">
                Strong technical skills don't always translate to strong leadership capability. Many dealerships lack structured development programmes for managers.
              </p>
            </div>
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-summit-navy mb-3">Summit Solution</h3>
              <p className="text-summit-grey">
                Tailored leadership development programmes that build capability, confidence, and commercial acumen. We mentor managers to become effective leaders who drive team performance.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-summit-navy mb-3">Commercial Outcome</h3>
              <p className="text-summit-grey">
                Stronger leadership teams, improved staff retention, enhanced motivation, and a culture of accountability and performance.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* Divider */}
      <div className="border-t border-gray-200"></div>

      {/* Sales Event Programmes */}
      <Section>
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold text-summit-navy-dark mb-6">High-Impact Sales Events</h2>
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-summit-navy mb-3">The Challenge</h3>
              <p className="text-summit-grey">
                Month-end, quarter-end, and seasonal challenges require focused effort and leadership support to maximise results. Internal teams are often stretched.
              </p>
            </div>
            <div className="mb-6">
              <h3 className="text-xl font-semibold text-summit-navy mb-3">Summit Solution</h3>
              <p className="text-summit-grey">
                Our sales event programmes provide on-site leadership, coaching, and team motivation during critical trading periods. We drive activity, support deal progression, and ensure results are delivered.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-summit-navy mb-3">Commercial Outcome</h3>
              <p className="text-summit-grey">
                Increased sales volumes, improved conversion rates, enhanced team morale, and immediate revenue impact during high-value trading periods.
              </p>
            </div>
          </div>
          <div>
            <ServiceCard title="Event Formats" items={salesEventServices} />
          </div>
        </div>
      </Section>

      {/* Divider */}
      <div className="border-t border-gray-200"></div>

      {/* Key Differentiator */}
      <Section background="grey">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-summit-navy-dark mb-6">What Sets Summit Apart</h2>
          <div className="bg-summit-navy-light text-white p-8 rounded-sm">
            <p className="text-lg leading-relaxed mb-4">
              Unlike traditional consultancies that deliver reports and recommendations before departing, Summit MCG provides hands-on, on-site partnership throughout delivery.
            </p>
            <p className="text-lg leading-relaxed">
              We work alongside your management team, guiding activity, coaching in real time, and ensuring that recommendations translate into measurable commercial results.
            </p>
          </div>
        </div>
      </Section>

      {/* Divider */}
      <div className="border-t border-gray-200"></div>

      {/* CTA */}
      <Section>
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-summit-navy-dark mb-4">Discuss Your Requirements</h2>
          <p className="text-lg text-summit-grey mb-8">
            Every dealership is unique. Let's discuss how Summit MCG can support your specific performance objectives.
          </p>
          <Button to="/contact">Get in Touch</Button>
        </div>
      </Section>
    </>
  );
};

export default Services;